import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { storiesAPI } from '../../../services/api';
import { getUserPlaceholderImage } from '../../../utils/imageUtils';

const StoryReactionsPanel = ({ storyId, isOpen, onClose, onNavigate, currentUserId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [reactions, setReactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Drag-to-close state
    const panelRef = useRef(null);
    const dragStartY = useRef(null);
    const currentTranslateY = useRef(0);

    // Fetch reactions when the panel opens
    useEffect(() => {
        if (!isOpen || !storyId) return;

        setReactions([]);
        setError(null);
        setIsLoading(true);

        storiesAPI.getStoryReactions(storyId)
            .then((res) => {
                // Support multiple possible response shapes
                const data = res?.data?.data || res?.data?.reactions || res?.data || [];
                setReactions(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.error('Failed to load story reactions:', err);
                setError(t('stories.loadReactionsError') || 'Could not load reactions.');
            })
            .finally(() => setIsLoading(false));
    }, [isOpen, storyId, t]);

    // ── Drag-to-close handlers ──────────────────────────────────
    const handleDragStart = useCallback((clientY) => {
        dragStartY.current = clientY;
        currentTranslateY.current = 0;
        if (panelRef.current) {
            panelRef.current.style.transition = 'none';
        }
    }, []);

    const handleDragMove = useCallback((clientY) => {
        if (dragStartY.current === null) return;
        const delta = clientY - dragStartY.current;
        if (delta < 0) return; // Don't allow dragging up
        currentTranslateY.current = delta;
        if (panelRef.current) {
            panelRef.current.style.transform = `translateY(${delta}px)`;
        }
    }, []);

    const handleDragEnd = useCallback(() => {
        if (dragStartY.current === null) return;
        dragStartY.current = null;
        if (panelRef.current) {
            panelRef.current.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
        }
        if (currentTranslateY.current > 120) {
            onClose();
        } else {
            if (panelRef.current) {
                panelRef.current.style.transform = 'translateY(0)';
            }
        }
        currentTranslateY.current = 0;
    }, [onClose]);

    // Mouse / touch event adapters for the drag handle
    const onMouseDown = (e) => { e.stopPropagation(); handleDragStart(e.clientY); };
    const onMouseMove = (e) => handleDragMove(e.clientY);
    const onMouseUp = () => handleDragEnd();
    const onTouchStart = (e) => { e.stopPropagation(); handleDragStart(e.touches[0].clientY); };
    const onTouchMove = (e) => handleDragMove(e.touches[0].clientY);
    const onTouchEnd = () => handleDragEnd();

    // Helper: build display name from various backend shapes
    const getReactionUserName = (reaction) => {
        const user = reaction.user || reaction;
        return user.name ||
            [user.first_name, user.last_name].filter(Boolean).join(' ') ||
            user.username ||
            'User';
    };

    const getReactionUserAvatar = (reaction) => {
        const user = reaction.user || reaction;
        return user.avatar || user.profile_image_url || user.image || getUserPlaceholderImage(user);
    };

    const reactionCount = reactions.length;

    return (
        <>
            <div
                ref={panelRef}
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 300,
                    height: '65%',
                    borderRadius: '20px 20px 0 0',
                    background: 'rgba(18, 18, 18, 0.97)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transform: isOpen ? 'translateY(0)' : 'translateY(110%)',
                    transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
                    willChange: 'transform',
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag Handle */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '12px 16px 8px',
                        cursor: 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <div style={{
                        width: '40px',
                        height: '4px',
                        borderRadius: '2px',
                        background: 'rgba(255,255,255,0.3)',
                        marginBottom: '14px',
                    }} />

                    {/* Header Row */}
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Heart Icon */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#ff416c"
                                stroke="none" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                            <span style={{
                                color: 'rgba(255,255,255,0.9)',
                                fontFamily: '"Red Hat Display", sans-serif',
                                fontSize: '16px',
                                fontWeight: '600',
                            }}>
                                {t('stories.likes') || 'Likes'}
                            </span>
                            {!isLoading && !error && (
                                <span style={{
                                    background: 'rgba(255,255,255,0.12)',
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '12px',
                                    fontFamily: '"Red Hat Display", sans-serif',
                                    fontWeight: '500',
                                    padding: '2px 8px',
                                    borderRadius: '20px',
                                }}>
                                    {reactionCount}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                all: 'unset',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            aria-label="Close reactions"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="rgba(255,255,255,0.7)" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '0 16px' }} />

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {isLoading && (
                        <div style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '44px', height: '44px', borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.08)',
                                        animation: 'pulse 1.4s ease-in-out infinite',
                                    }} />
                                    <div style={{
                                        height: '14px', borderRadius: '7px', flex: 1,
                                        background: 'rgba(255,255,255,0.08)',
                                        animation: 'pulse 1.4s ease-in-out infinite',
                                        animationDelay: `${i * 0.1}s`,
                                    }} />
                                </div>
                            ))}
                        </div>
                    )}

                    {!isLoading && error && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: '8px', padding: '40px 24px', textAlign: 'center',
                        }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <circle cx="12" cy="16" r="0.8" fill="currentColor" stroke="none" />
                            </svg>
                            <p style={{
                                color: 'rgba(255,255,255,0.4)', fontSize: '14px',
                                fontFamily: '"Red Hat Display", sans-serif', margin: 0,
                            }}>{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && reactionCount === 0 && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: '12px', padding: '48px 24px',
                        }}>
                            <div style={{
                                width: '64px', height: '64px', borderRadius: '50%',
                                background: 'rgba(255,255,255,0.05)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                    stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                </svg>
                            </div>
                            <p style={{
                                color: 'rgba(255,255,255,0.4)', fontSize: '14px',
                                fontFamily: '"Red Hat Display", sans-serif', margin: 0, textAlign: 'center',
                            }}>
                                {t('stories.noReactions') || 'No likes yet'}
                            </p>
                        </div>
                    )}

                    {!isLoading && !error && reactionCount > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: '8px 0' }}>
                            {reactions.map((reaction, idx) => {
                                const user = reaction.user || reaction;
                                return (
                                    <li
                                        key={reaction.id || idx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '10px 16px',
                                            transition: 'background 0.15s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            if (user.id) {
                                                onNavigate?.();
                                                const isCurrentUser = String(user.id) === String(currentUserId);
                                                navigate(isCurrentUser ? '/social/profile' : `/profile/${user.id}`);
                                            }
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <div style={{
                                            width: '44px', height: '44px', borderRadius: '50%',
                                            overflow: 'hidden', flexShrink: 0,
                                            border: '2px solid rgba(255,255,255,0.1)',
                                        }}>
                                            <img
                                                src={getReactionUserAvatar(reaction)}
                                                alt={getReactionUserName(reaction)}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => { e.target.src = getUserPlaceholderImage(reaction.user || reaction); }}
                                            />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{
                                                margin: 0,
                                                color: 'rgba(255,255,255,0.9)',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                fontFamily: '"Red Hat Display", sans-serif',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {getReactionUserName(reaction)}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {/* Pulse keyframe for skeleton loader */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
        </>
    );
};

export default StoryReactionsPanel;
